import { html, LitElement } from 'lit';
import { live } from 'lit/directives/live.js';
import { TagKey } from '@cof/tag-catalog-fs-personal-mfe';
import { DebugService } from '../../../../shared/services/debug.service.js';
import { REFI_PERSONAL_CONST } from '../../../../shared/constants/app.constants.js';
import { globalStyle } from '../../../../global-styles.style.js';
import { style } from './edit-phone.style.js';
import { stratum } from '../../../../../constants/stratum.js';

class RefiEditPhone extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiEditPhone');
  }

  static properties = {
    validPhoneType: { type: Boolean, reflect: true },
    showTcpaConsent: { type: Boolean, reflect: true },
    showTcpaDisclosure: { type: Boolean, reflect: true },
    hasTcpaConsent: { type: Boolean, reflect: true },
    phoneNumber: { type: String, reflect: true },
    applicant: { type: Object, reflect: true },
    textOptInError: { type: Boolean, reflect: true },
  };

  static styles = [globalStyle, style];

  connectedCallback() {
    super.connectedCallback();
    this.phoneNumber = this.applicant.phoneNumber;
    if (!this.applicant.phoneType) {
      // only change if we were able to retrieve tcpa consent in the first place
      if (this.applicant.hasTcpaConsent !== null) {
        this.applicant.hasTcpaConsent = false;
      }
      this.applicant.phoneType = 'HOME';
    }
    this.hasTcpaConsent = this.applicant.hasTcpaConsent;
    stratum.publishTag(TagKey.MOBILE_NUMBER_MODULE_TCPA_CONSENT_BOX_SHOWN, {
      replacements: {
        value: this.applicant.hasTcpaConsent ? 'checked' : 'unchecked',
      },
    });
  }

  render() {
    return html` <div class="checkout-phone-edit">
        <fs-phone-number-field
          id="checkout-phone-edit"
          autocomplete="tel"
          name="phone"
          type="tel"
          value-missing-message="Please enter a valid phone number"
          maxLength="14"
          pattern="^([2-9]\\d{2})-?[2-9]\\d{2}-?\\d{4}$"
          required
          helper-text="Changes to your phone number will be applied to all of your Capital One accounts"
          @change="${(ev) => {
            if (ev.target.value.startsWith('1-')) {
              ev.target.value = ev.target.value.slice(2);
            }
            this.phoneNumber = ev.target.value;
            this.changed('phoneNumber', ev);
            this.shadowRoot.getElementById(
              'checkout-tcpa-consent-edit'
            ).checked = false;
            this.hasTcpaConsent = false;
            this.applicant.hasTcpaConsent = false;
            this.textOptInError = false;
            this.shadowRoot.getElementById('tcpa-disclosure').hidden = true;
          }}"
          @blur="${(ev) => {
            this.dispatchEvent(new CustomEvent('unsaved-fields'));
            this.changed('phoneNumber', ev);
            if (ev.target.validity.valid) {
              stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_EDITED_FIELD, {
                replacements: { value: 'phone' },
              });
            } else {
              stratum.publishTag(
                TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
                {
                  replacements: {
                    value: 'Please enter a valid phone number',
                  },
                }
              );
            }
          }}"
          value="${this.phoneNumber}"
          pattern-mismatch-message="Please enter a valid phone number"
        >
          Phone number
        </fs-phone-number-field>
        <fs-select
          id="checkout-phone-select"
          name="phone-type-select"
          @change="${(ev) => {
            this.changed('phoneType', ev);
            this.textOptInError = false;
            this.showTcpaConsent = this.applicant.phoneType === 'MOBILE';
            this.validPhoneType =
              ev.target.value !== null && ev.target.value !== '';
            if (this.validPhoneType) {
              if (
                this.applicant.phoneType !== 'MOBILE' &&
                this.applicant.hasTcpaConsent !== null
              ) {
                this.applicant.hasTcpaConsent = false;
              } else {
                this.shadowRoot.getElementById('tcpa-disclosure').hidden =
                  !this.showTcpaDisclosure;
              }
              stratum.publishTag(
                TagKey.PERSONAL_COMMUNICATION_PHONE_NUMBER_TYPE,
                { replacements: { value: ev.target.value } }
              );
            }
          }}"
          @blur="${(ev) => {
            if (ev.target.validity.valid) {
              stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_EDITED_FIELD, {
                replacements: { value: 'phoneType' },
              });
            } else {
              stratum.publishTag(
                TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
                {
                  replacements: {
                    value:
                      'Please select a phone number type from the dropdown menu.',
                  },
                }
              );
            }
          }}"
          value="${this.applicant.phoneType}"
          required
        >
          <span slot="label">Phone type</span>
          <option value="">Select a phone type</option>
          ${REFI_PERSONAL_CONST.PHONE_TYPES.map(
            (phoneType) => html` <option
              ?selected="${this.applicant.phoneType ===
              phoneType.toUpperCase()}"
              value="${phoneType.toUpperCase()}"
            >
              ${phoneType}
            </option>`
          )}
        </fs-select>
      </div>
      <div
        ?hidden="${this.hasTcpaConsent == null ||
        this.applicant.phoneType !== 'MOBILE'}"
      >
        <fs-checkbox
          id="checkout-tcpa-consent-edit"
          name="tcpaConsent"
          form-field-alignment="start"
          value="${this.hasTcpaConsent}"
          .checked="${this.hasTcpaConsent}"
          ?hidden="${this.textOptInError}"
          @change="${(ev) => {
            this.applicant.hasTcpaConsent = ev.target.checked;
            this.showTcpaDisclosure = ev.target.checked;
            this.shadowRoot.getElementById('tcpa-disclosure').hidden =
              !this.applicant.hasTcpaConsent ||
              !this.showTcpaDisclosure ||
              this.textOptInError;
            stratum.publishTag(TagKey.PERSONAL_COMMUNICATION_SMS_OPT_ON_PAGE, {
              replacements: { value: this.applicant.hasTcpaConsent.toString() },
            });
          }}"
        >
          <fs-body-text size="normal" variant="primary" class="tcpaConsentText">
            I want to receive text messages about my application (optional)
          </fs-body-text>
        </fs-checkbox>
        <fs-alert id="optInFailure" ?hidden="${!this.textOptInError}">
          We were able to save your personal information, but you might not
          receive text messages about your refinance application because this
          mobile number is receiving messages for a different Capital One login.
          If you want to get texts about your refinance application, please
          enter a different mobile number.
        </fs-alert>
        <p
          id="tcpa-disclosure"
          class="disclosure"
          ?hidden="${!this.applicant.hasTcpaConsent ||
          !this.showTcpaDisclosure ||
          live(this.textOptInError)}"
        >
          By clicking this box, I agree to receive autodialed, artificial and/or
          prerecorded calls, texts, and voicemails from Capital One at this
          number.<br /><br />These calls and messages will be about my
          relationship with Capital One. This includes the handling, servicing,
          and billing of any Capital One account. Message and data rates may
          apply.
        </p>
      </div>`;
  }

  changed(name, ev) {
    this.applicant[name] = ev?.target?.value.trim();
    this.dispatchEvent(
      new CustomEvent('validity', {
        detail: { name, isValid: ev.target.validity.valid },
      })
    );
  }
}

customElements.define('refi-edit-phone', RefiEditPhone);
